import { DOCUMENT, NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';

import { NgScrollbar } from 'ngx-scrollbar';
import MetisMenu from 'metismenujs';

import { MENU } from './menu';
import { MenuItem } from './menu.model';
import { MasterService } from '../../../master.service'

import { FeatherIconDirective } from '../../../core/feather-icon/feather-icon.directive';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterLink, 
    RouterLinkActive, 
    NgScrollbar, 
    NgClass, 
    FeatherIconDirective, 
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, AfterViewInit {

  @ViewChild('sidebarToggler') sidebarToggler: ElementRef;

  menuItems: MenuItem[] = [];
  @ViewChild('sidebarMenu') sidebarMenu: ElementRef;

  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2, router: Router, private masterService : MasterService) { 
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {

        /**
         * Activating the current active item dropdown
         */
        this._activateMenuDropdown();

        /**
         * closing the sidebar
         */
        if (window.matchMedia('(max-width: 991px)').matches) {
          this.document.body.classList.remove('sidebar-open');
        }

      }
    });
  }

  ngOnInit(): void {
    // Filter menu items based on LIST permissions
    this.menuItems = this.filterMenuItemsByPermission(MENU);

    /**
     * Sidebar-folded on desktop (min-width:992px and max-width: 1199px)
     */
    const desktopMedium = window.matchMedia('(min-width:992px) and (max-width: 1199px)');
    desktopMedium.addEventListener('change', () => {
      this.iconSidebar;
    });
    this.iconSidebar(desktopMedium);
  }

  /**
   * Filter menu items based on LIST permissions
   * Maps each menu item to its feature code and checks if user has LIST permission
   * Also hides main menu titles if no submenus have permission
   */
  filterMenuItemsByPermission(menuItems: MenuItem[]): MenuItem[] {
    // Check if user is admin - if yes, show all menu items
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (isAdmin) {
      return menuItems;
    }
    
    // Mapping of menu labels to feature codes for permission checking
    const featureCodeMap: { [key: string]: string } = {
      // Masters
      'Country': 'MASTER.COUNTRY.LIST',
      'Currency': 'MASTER.CURRENCY.LIST',
      'MSISDN': 'MASTER.MSISDN.LIST',
      'Loan Remainder': 'MASTER.REMAINDER.LIST',
      'Charges': 'MASTER.CHARGES.LIST',
      'Denomination': 'MASTER.DENOMINATION.LIST',
      'Tier Slab Mapping': 'MASTER.TIERSLABMAPPING.LIST',
      'Aggregator': 'MASTER.AGGREGATOR.LIST',
      'Trusted Tier Group': 'MASTER.TRUSTEDTIERGROUP.LIST',
      'RFM Rules': 'MASTER.RFMRULES.LIST',
      'System Limits': 'MASTER.SYSTEMLIMIT.LIST',
      'Blacklisted Customers': 'MASTER.BLACKLISTEDCUSTOMERS.LIST',
      'Bar / Unbar Customers': 'MASTER.BARUNBAR.LIST',
      // EWallet
      'Disbursement': 'EWALLET.DISBURSEMENT.LIST',
      'Collection': 'EWALLET.COLLECTION.LIST',
      // User Management
      'Users': 'USERMGMT.USER.LIST',
      'Roles': 'USERMGMT.ROLE.LIST',
      // Reports
      'Agents': 'REPORTS.AGENTS.LIST',
      'Merchants': 'REPORTS.MERCHANTS.LIST',
      'Subscriber': 'REPORTS.SUBSCRIBER.LIST',
      'Loans': 'REPORTS.LOANS.LIST',
      'Debt Collection Log': 'REPORTS.DEBTCOLLECTIONLOG.LIST',
      'Consolidated Loan': 'REPORTS.CONSOLIDATEDLOAN.LIST',
      'Loan Eligibility Log': 'REPORTS.LOANELIGIBILITYLOG.LIST',
      // Reuses the existing AI Engine "Loan Information" feature/RBAC rather than a new Reports.* code.
      'Loan Information Log': 'AIENGINE.LOANINFORMATION.LIST',
      'File Transaction': 'REPORTS.FILETRANSACTION.LIST',
      'Agent Transaction': 'REPORTS.AGENTTRANSACTION.LIST',
      'Merchant Transaction': 'REPORTS.MERCHANTTRANSACTION.LIST',
      'Subscriber Transaction': 'REPORTS.SUBSCRIBERTRANSACTION.LIST',
      // Notifications
      'Scheduled SMS': 'NOTIFICATION.SMS-SCHEDULED.LIST',
      'SMS Request': 'NOTIFICATION.SMS.LIST',
      'Email Notification': 'NOTIFICATION.EMAIL-REQUEST.LIST',
      // Settings
      'Settings': 'SETTINGS.SETTINGS.LIST',
      'Payment History & Loyalty Eligibility': 'SETTINGS.PAYMENTHISTORYLADDER.LIST',
      // AI Engine
      'Loan Eligibility': 'AIENGINE.LOANELIGIBILITY.LIST',
      'Loan Information': 'AIENGINE.LOANINFORMATION.LIST',
      'Balance Enquiry': 'AIENGINE.BALANCEENQUIRY.LIST',
      'Manual Debt Collection': 'AIENGINE.MANUALDEBTCOLLECTION.LIST',
      'Credit Provision': 'AIENGINE.CREDITPROVISION.LIST',
      'Payment Collection': 'AIENGINE.PAYMENTCOLLECTION.LIST',
      'Collection Confirmation': 'AIENGINE.COLLECTIONCONFIRMATION.LIST',
      'Batch Testing': 'AIENGINE.BATCHTESTING.LIST',
      // Support
      'Loan Search': 'SUPPORT.LOANSEARCH.LIST',
      'Tickets': 'SUPPORT.TICKETS.LIST',
      // WhatsApp. One feature per screen, matching every other module - so a compliance user can be
      // given the opt-out list without also being able to dispatch a campaign to thousands of
      // borrowers.
      'WhatsApp Campaign': 'WHATSAPP.CAMPAIGN.LIST',
      'Opt-Out List': 'WHATSAPP.OPTOUT.LIST',
      'WhatsApp Reports': 'WHATSAPP.REPORTS.LIST'
    };

    // First pass: Filter individual menu items based on permissions
    const filteredItems = menuItems.filter((item) => {
      // For titles, we'll handle them in the second pass
      if (item.isTitle) {
        return true;
      }

      // Dashboard - check for LIST permission
      if (item.label === 'Dashboard') {
        return this.masterService.checkFeatureBoolean('DASHBOARD.DASHBOARD.LIST');
      }

      // Skip items without labels
      if (!item.label) {
        return false;
      }

      // Check if this menu item has a feature code mapping.
      //
      // Labels are normalised first: a long label may carry non-breaking spaces to control where it
      // wraps in the sidebar, and those would never match a key typed with ordinary spaces. Since an
      // unmapped item is hidden rather than shown, that mismatch would silently remove the screen
      // for every non-admin user - and look like a permissions problem rather than a typo.
      const featureCode = featureCodeMap[item.label.replace(/ /g, ' ')];
      
      if (featureCode) {
        // Check if user has LIST permission for this feature
        return this.masterService.checkFeatureBoolean(featureCode);
      }

      // If no feature code mapping, hide the item (except Dashboard which is handled above)
      // This ensures only items with explicit permissions are shown
      return false;
    });

    // Second pass: Remove titles if no items after them have permission (until next title or end)
    const finalItems: MenuItem[] = [];
    let i = 0;
    
    while (i < filteredItems.length) {
      const currentItem = filteredItems[i];
      
      if (currentItem.isTitle) {
        // Check if there are any visible items after this title until the next title
        let hasVisibleItems = false;
        let j = i + 1;
        
        while (j < filteredItems.length && !filteredItems[j].isTitle) {
          if (filteredItems[j].label) {
            hasVisibleItems = true;
            break;
          }
          j++;
        }
        
        // Only add the title if there are visible items after it
        if (hasVisibleItems) {
          finalItems.push(currentItem);
        }
        i++;
      } else {
        // Add non-title items
        finalItems.push(currentItem);
        i++;
      }
    }

    return finalItems;
  }

  ngAfterViewInit() {
    // activate menu items
    new MetisMenu(this.sidebarMenu.nativeElement);
    
    this._activateMenuDropdown();
  }

  displayMenu(label : any) {
    console.log("display menu called" , label);
    if( label == 'Country'){

      if (this.masterService.menuChecking('MASTER.COUNTRY') == true )
        return true;

    }

    return false;

  }

  /**
   * Toggle the sidebar when the hamburger button is clicked
   */
  toggleSidebar(e: Event) {
    this.sidebarToggler.nativeElement.classList.toggle('active');
    if (window.matchMedia('(min-width: 992px)').matches) {
      e.preventDefault();
      this.document.body.classList.toggle('sidebar-folded');
    } else if (window.matchMedia('(max-width: 991px)').matches) {
      e.preventDefault();
      this.document.body.classList.toggle('sidebar-open');
    }
  }


  /**
   * Open the sidebar on hover when it is in a folded state
   */
  operSidebarFolded() {
    if (this.document.body.classList.contains('sidebar-folded')){
      this.document.body.classList.add("open-sidebar-folded");
    }
  }


  /**
   * Fold sidebar after mouse leave (in folded state)
   */
  closeSidebarFolded() {
    if (this.document.body.classList.contains('sidebar-folded')){
      this.document.body.classList.remove("open-sidebar-folded");
    }
  }

  /**
   * Sidebar folded on desktop screens with a width between 992px and 1199px
   */
  iconSidebar(mq: MediaQueryList) {
    if (mq.matches) {
      this.document.body.classList.add('sidebar-folded');
    } else {
      this.document.body.classList.remove('sidebar-folded');
    }
  }


  /**
   * Returns true or false depending on whether the given menu item has a child
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }


  /**
   * Reset the menus, then highlight the currently active menu item
   */
  _activateMenuDropdown() {
    this.resetMenuItems();
    this.activateMenuItems();
  }


  /**
   * Resets the menus
   */
  resetMenuItems() {

    const links = document.getElementsByClassName('nav-link-ref');
    
    for (let i = 0; i < links.length; i++) {
      const menuItemEl = links[i];
      menuItemEl.classList.remove('mm-active');
      const parentEl = menuItemEl.parentElement;

      if (parentEl) {
          parentEl.classList.remove('mm-active');
          const parent2El = parentEl.parentElement;
          
          if (parent2El) {
            parent2El.classList.remove('mm-show');
          }

          const parent3El = parent2El?.parentElement;
          if (parent3El) {
            parent3El.classList.remove('mm-active');

            if (parent3El.classList.contains('side-nav-item')) {
              const firstAnchor = parent3El.querySelector('.side-nav-link-a-ref');

              if (firstAnchor) {
                firstAnchor.classList.remove('mm-active');
              }
            }

            const parent4El = parent3El.parentElement;
            if (parent4El) {
              parent4El.classList.remove('mm-show');

              const parent5El = parent4El.parentElement;
              if (parent5El) {
                parent5El.classList.remove('mm-active');
              }
            }
          }
      }
    }
  };


  /**
   * Toggles the state of the menu items
   */
  activateMenuItems() {

    const links: any = document.getElementsByClassName('nav-link-ref');

    let menuItemEl = null;
    
    for (let i = 0; i < links.length; i++) {
      // tslint:disable-next-line: no-string-literal
        if (window.location.pathname === links[i]['pathname']) {
          
            menuItemEl = links[i];
            
            break;
        }
    }

    if (menuItemEl) {
        menuItemEl.classList.add('mm-active');
        const parentEl = menuItemEl.parentElement;

        if (parentEl) {
            parentEl.classList.add('mm-active');

            const parent2El = parentEl.parentElement;
            if (parent2El) {
                parent2El.classList.add('mm-show');
            }

            const parent3El = parent2El.parentElement;
            if (parent3El) {
                parent3El.classList.add('mm-active');

                if (parent3El.classList.contains('side-nav-item')) {
                    const firstAnchor = parent3El.querySelector('.side-nav-link-a-ref');

                    if (firstAnchor) {
                        firstAnchor.classList.add('mm-active');
                    }
                }

                const parent4El = parent3El.parentElement;
                if (parent4El) {
                    parent4El.classList.add('mm-show');

                    const parent5El = parent4El.parentElement;
                    if (parent5El) {
                        parent5El.classList.add('mm-active');
                    }
                }
            }
        }
    }
  };


}
